import { useEffect, useState } from "react";
import { createSale, fetchGraded, fetchSales, fetchSealed, fetchSingles } from "../api";
import type { AppConfig, GradedItem, Sale, SealedItem, SinglesItem } from "../types";

function fmt(v: string | null | undefined) {
  if (!v) return "—";
  const n = parseFloat(v);
  return isNaN(n) ? v : n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

const MODE_OPTS = ["", "sealed", "singles", "graded"];
const PLATFORM_OPTS = ["", "tcgplayer", "manapool", "ebay", "facebook", "local", "other"];

export default function SalesView({ cfg }: { cfg: AppConfig }) {
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [modeFilter, setModeFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  function load() {
    setLoading(true);
    fetchSales(cfg, { mode: modeFilter || undefined })
      .then(setSales)
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(load, [cfg, modeFilter]);

  const totalNet = sales.reduce((s, sale) => s + parseFloat(sale.net_proceeds), 0);

  return (
    <>
      <div className="view-header">
        <span className="view-title">Sales</span>
        <span className="view-count">{sales.length}</span>
        <span className="view-total">
          Net Proceeds <strong style={{ color: "var(--good)" }}>
            {totalNet.toLocaleString("en-US", { style: "currency", currency: "USD" })}
          </strong>
        </span>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Record Sale</button>
      </div>

      <div className="filter-bar">
        <div className="status-tabs">
          {MODE_OPTS.map((m) => (
            <button key={m || "all"} className={`status-tab ${modeFilter === m ? "active" : ""}`} onClick={() => setModeFilter(m)}>
              {m || "All"}
            </button>
          ))}
        </div>
      </div>

      <div className="table-wrap">
        {loading && <div className="state-container"><div className="spinner" /></div>}
        {err && <div className="state-container"><p style={{ color: "var(--bad)" }}>{err}</p></div>}
        {!loading && !err && (
          <table>
            <thead>
              <tr>
                <th>Mode</th>
                <th>Item ID</th>
                <th>Platform</th>
                <th>Date</th>
                <th style={{ textAlign: "right" }}>Sale Price</th>
                <th style={{ textAlign: "right" }}>Fees</th>
                <th style={{ textAlign: "right" }}>Shipping Out</th>
                <th style={{ textAlign: "right" }}>Net Proceeds</th>
                <th>Buyer</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((s) => (
                <tr key={s.id}>
                  <td><span className="badge" style={{ background: "rgba(79,140,255,0.12)", color: "var(--accent)", fontSize: 11 }}>{s.mode}</span></td>
                  <td><span className="field-mono">{s.item_id.slice(0, 8)}…</span></td>
                  <td style={{ fontSize: 12 }}>{s.platform}</td>
                  <td style={{ fontSize: 12 }}>{s.sold_at.slice(0, 10)}</td>
                  <td className="td-money">{fmt(s.sale_price)}</td>
                  <td className="td-money" style={{ color: "var(--bad)" }}>{fmt(s.platform_fees)}</td>
                  <td className="td-money" style={{ color: "var(--bad)" }}>{fmt(s.shipping_out)}</td>
                  <td className="td-money" style={{ fontWeight: 600, color: "var(--good)" }}>{fmt(s.net_proceeds)}</td>
                  <td style={{ fontSize: 12, color: "var(--text-dim)" }}>{s.buyer ?? "—"}</td>
                </tr>
              ))}
              {sales.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: "center", color: "var(--text-dim)", padding: 32 }}>No sales yet</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <RecordSaleModal
          cfg={cfg}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </>
  );
}

type AnyItem = (SealedItem & { _mode: "sealed" }) | (SinglesItem & { _mode: "singles" }) | (GradedItem & { _mode: "graded" });

function getItemLabel(item: AnyItem): string {
  if (item._mode === "sealed") return (item as SealedItem).product_name;
  if (item._mode === "singles") return (item as SinglesItem).card_name;
  return (item as GradedItem).card_name;
}

function RecordSaleModal({ cfg, onClose, onCreated }: { cfg: AppConfig; onClose: () => void; onCreated: () => void }) {
  const [mode, setMode] = useState("singles");
  const [itemSearch, setItemSearch] = useState("");
  const [availableItems, setAvailableItems] = useState<AnyItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [selectedItem, setSelectedItem] = useState<AnyItem | null>(null);
  const [platform, setPlatform] = useState("tcgplayer");
  const [soldAt, setSoldAt] = useState(new Date().toISOString().slice(0, 10));
  const [salePrice, setSalePrice] = useState("");
  const [fees, setFees] = useState("0");
  const [shippingOut, setShippingOut] = useState("0");
  const [buyer, setBuyer] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setSelectedItem(null);
    setAvailableItems([]);
    setLoadingItems(true);
    const load = async () => {
      try {
        if (mode === "sealed") {
          const items = await fetchSealed(cfg, { status: "in_stock" });
          setAvailableItems(items.map((i) => ({ ...i, _mode: "sealed" as const })));
        } else if (mode === "singles") {
          const items = await fetchSingles(cfg, { status: "in_stock" });
          setAvailableItems(items.map((i) => ({ ...i, _mode: "singles" as const })));
        } else {
          const items = await fetchGraded(cfg, { status: "in_stock" });
          setAvailableItems(items.map((i) => ({ ...i, _mode: "graded" as const })));
        }
      } catch {}
      setLoadingItems(false);
    };
    void load();
  }, [cfg, mode]);

  const filtered = itemSearch
    ? availableItems.filter((i) => getItemLabel(i).toLowerCase().includes(itemSearch.toLowerCase()))
    : availableItems.slice(0, 20);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedItem) { setErr("Select an item"); return; }
    setSaving(true);
    setErr(null);
    try {
      await createSale(cfg, {
        mode,
        item_id: selectedItem.id,
        platform,
        sold_at: soldAt,
        sale_price: salePrice,
        platform_fees: fees,
        shipping_out: shippingOut,
        buyer: buyer || null,
        notes: notes || null,
      });
      onCreated();
    } catch (e) {
      setErr((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer">
        <div className="drawer-header">
          <h2>Record Sale</h2>
          <button className="drawer-close" onClick={onClose}>✕</button>
        </div>
        <div className="drawer-body">
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Mode *</label>
              <select className="form-select" value={mode} onChange={(e) => setMode(e.target.value)}>
                {["sealed", "singles", "graded"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Item *</label>
              <input
                className="form-input"
                value={selectedItem ? getItemLabel(selectedItem) : itemSearch}
                onChange={(e) => { setItemSearch(e.target.value); setSelectedItem(null); }}
                placeholder={loadingItems ? "Loading…" : "Search or select item…"}
              />
              {!selectedItem && filtered.length > 0 && (
                <div style={{ background: "var(--bg-elev-2)", border: "1px solid var(--border)", borderRadius: 6, maxHeight: 200, overflowY: "auto", marginTop: 4 }}>
                  {filtered.map((item) => (
                    <div
                      key={item.id}
                      style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid var(--border)" }}
                      className="autocomplete-item"
                      onClick={() => { setSelectedItem(item); setItemSearch(""); }}
                    >
                      {getItemLabel(item)}
                      <span style={{ float: "right", fontSize: 11, color: "var(--text-dim)" }}>
                        {"cost_per_unit" in item ? fmt(item.cost_per_unit) : fmt((item as SinglesItem | GradedItem).cost_basis)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Platform *</label>
              <select className="form-select" value={platform} onChange={(e) => setPlatform(e.target.value)}>
                {PLATFORM_OPTS.filter(Boolean).map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Sale Date *</label>
              <input className="form-input" type="date" value={soldAt} onChange={(e) => setSoldAt(e.target.value)} required />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Sale Price *</label>
                <input className="form-input" value={salePrice} onChange={(e) => setSalePrice(e.target.value)} placeholder="0.00" required />
              </div>
              <div className="form-group">
                <label className="form-label">Platform Fees</label>
                <input className="form-input" value={fees} onChange={(e) => setFees(e.target.value)} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label className="form-label">Shipping Out</label>
                <input className="form-input" value={shippingOut} onChange={(e) => setShippingOut(e.target.value)} placeholder="0.00" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Buyer</label>
              <input className="form-input" value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="Username" />
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>

            {err && <p style={{ color: "var(--bad)", fontSize: 12 }}>{err}</p>}

            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Record Sale"}</button>
              <button className="btn" type="button" onClick={onClose}>Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
